import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { MdCheckCircle, MdVisibility, MdVisibilityOff } from 'react-icons/md'
import { useAuth } from '@/hooks/useAuth'
import logger from '@/utils/logger'
import ValidationSummary from '@/components/common/ValidationSummary'
import PasswordStrength from '@/components/auth/login/PasswordStrength'
import type { ServerError, ValidationErrors } from '@/types'

/**
 * Reset-password step. Rendered inside the login-page shell so it
 * shares the same two-column popup, brand panel, and footer as the
 * primary login flow.
 *
 * Mirrors LoginClaimStep: navy heading, subheading, password +
 * confirm with show/hide toggle, PasswordStrength rule-list, primary
 * full-width submit, and a Back footer link. Submit is gated by the
 * same strongEnough && passwordsMatch contract used in the claim
 * step so the customer can't fire a request the backend will 422.
 */

interface FormData {
  newPassword: string
  confirmPassword: string
}

const INITIAL_FORM_STATE: FormData = {
  newPassword: '',
  confirmPassword: '',
}

function ResetPasswordForm(): React.JSX.Element {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { resetPassword } = useAuth()
  const token = searchParams.get('token')
  const email = searchParams.get('email')

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_STATE)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({})
  const [showPassword, setShowPassword] = useState(false)
  const [strongEnough, setStrongEnough] = useState(false)

  useEffect(() => {
    if (!token || !email) {
      setErrorMessage(
        'Invalid or missing reset link. Please request a new password reset.',
      )
    }
  }, [token, email])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const passwordsMatch =
    formData.newPassword.length > 0 &&
    formData.newPassword === formData.confirmPassword

  const validateForm = (): ValidationErrors => {
    const newErrors: ValidationErrors = {}
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required'
    } else if (!strongEnough) {
      newErrors.newPassword = 'Password does not meet the requirements'
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    return newErrors
  }

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault()
    setErrorMessage('')
    setErrors({})
    setValidationErrors({})

    if (!token || !email) {
      setErrorMessage(
        'Invalid reset link. Please request a new password reset.',
      )
      return
    }

    const newErrors = validateForm()
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsLoading(true)
    try {
      await resetPassword(token, email, formData.newPassword)
      setSuccess(true)
      setTimeout(() => navigate('/login'), 2500)
    } catch (error: unknown) {
      const resetError = error as ServerError
      logger.error('Reset password error:', resetError)

      if (resetError?.body) {
        const newValidationErrors: Record<string, string[]> = {}
        if (
          resetError.body.errors &&
          typeof resetError.body.errors === 'object'
        ) {
          Object.entries(resetError.body.errors).forEach(
            ([field, messages]) => {
              newValidationErrors[field] = Array.isArray(messages)
                ? messages
                : [messages as string]
            },
          )
        }
        setValidationErrors(newValidationErrors)
        setErrorMessage(
          resetError.body.message ||
            'Something went wrong. Please check the form and try again.',
        )
      } else {
        setErrorMessage(
          resetError.message ||
            'Failed to reset password. Please try again or request a new reset link.',
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="login-step login-step--neutral">
        <div className="login-step-icon login-step-icon--success">
          <MdCheckCircle size={48} />
        </div>
        <h1 className="login-step-heading">Password updated</h1>
        <p className="login-step-subheading">
          Your password has been reset. Taking you to the sign-in screen…
        </p>
      </div>
    )
  }

  const canSubmit = !!token && !!email && strongEnough && passwordsMatch

  return (
    <div className="login-step">
      <h1 className="login-step-heading">Reset your password</h1>
      <p className="login-step-subheading">
        Choose a new password for your McQueens portal account.
      </p>

      <ValidationSummary errors={validationErrors} message={errorMessage} />

      {token && email && (
        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              New password
            </label>
            <div className="password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className={`form-control form-control-lg ${errors.newPassword ? 'is-invalid' : ''}`}
                autoComplete="new-password"
                autoFocus
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((p) => !p)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
              </button>
            </div>
            {errors.newPassword && (
              <span className="form-text is-invalid">{errors.newPassword}</span>
            )}
            <PasswordStrength
              password={formData.newPassword}
              onValidityChange={setStrongEnough}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm new password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`form-control form-control-lg ${errors.confirmPassword ? 'is-invalid' : ''}`}
              autoComplete="new-password"
              disabled={isLoading}
            />
            {errors.confirmPassword && (
              <span className="form-text is-invalid">{errors.confirmPassword}</span>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg btn-block"
            disabled={isLoading || !canSubmit}
          >
            {isLoading ? 'Resetting…' : 'Reset password'}
          </button>
        </form>
      )}

      <div className="login-step-footer">
        <button
          type="button"
          className="btn btn-link"
          onClick={() => navigate('/login')}
        >
          Back
        </button>
      </div>
    </div>
  )
}

export default ResetPasswordForm
